import React, { useState } from "react";
import { renameFlaggedName, nameLooksFine } from "../services/userService";
import {
  NAME_REVIEW_LABEL,
  isNameFlagged,
  hasNameReview,
  flaggedLine,
  resolvedLine,
  refusalText,
  firstNameOf,
  looksFineTeachesLine,
  RENAME_TEACHES_LINE,
} from "./nameReviewUtils";

/**
 * "Name needs a look" on the member page (2.6, founder 2026-10-15; backend
 * controllers/admin.nameReview.js). Shown only for a new sign-up the name
 * check asked about: existing members never carry nameReview, so for them
 * this renders nothing.
 *
 * Two answers, one tap each: Looks fine (clears the flag), or Rename (type
 * the name, save). A rename runs the same rules as sign-up, so a name the
 * rules refuse comes back with the member's own message and nothing changes.
 * Other members see the new first word, in chat too. Nothing is sent to the
 * member either way. Either answer also tells the name check that the first
 * word (the new one, after a rename) is a real first name, so later sign-ups
 * with it are not asked. The server writes the audit row before the change
 * and refuses the change when it cannot; its message is shown as it comes.
 * The result lives in the PARENT, as the pause panel's
 * does, because the refresh after a change swaps the page for the loader.
 */
const NameReviewPanel = ({ user, canEdit, result, onChanged, onDismissResult, onUnauthorized }) => {
  const flagged = isNameFlagged(user);
  const review = (user && user.nameReview) || null;
  const who = firstNameOf(user && user.fullName) || "this member";
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!hasNameReview(user) && !result) return null;

  const fail = (err) => {
    const status = err && err.response && err.response.status;
    if (status === 401 && onUnauthorized) {
      onUnauthorized();
      return;
    }
    setError(refusalText(err));
  };

  const looksFine = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await nameLooksFine(user._id);
      const data = (res && res.data) || {};
      if (data.success === false) {
        setError(data.message || "The server refused this. Nothing was changed.");
        return;
      }
      if (onChanged) onChanged({ kind: "looks_fine", name: user.fullName, data });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const rename = async (e) => {
    e.preventDefault();
    const typed = name.trim();
    if (!typed) {
      setError("Type the name to save.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await renameFlaggedName(user._id, typed);
      const data = (res && res.data) || {};
      if (data.success === false) {
        setError(data.message || "The server refused this. Nothing was changed.");
        return;
      }
      setRenaming(false);
      setName("");
      if (onChanged) onChanged({ kind: "renamed", from: user.fullName, name: data.fullName, data });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="col-md-12 mb-3">
      <div className="border radius-8 px-16 py-12" data-testid="name-review-panel">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <label className="form-label fw-bold me-2 mb-0">Name:</label>
            {flagged ? (
              <span className="bg-warning-100 text-warning-700 px-8 py-2 radius-4 fw-medium" data-testid="name-review-line">
                {NAME_REVIEW_LABEL}
              </span>
            ) : (
              <span data-testid="name-review-line">{resolvedLine(review) || "Looked at."}</span>
            )}
          </div>
          {canEdit && flagged && !renaming && (
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-success btn-sm" onClick={looksFine} disabled={busy}>
                {busy ? "Saving..." : "Looks fine"}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => {
                  setRenaming(true);
                  setName((user && user.fullName) || "");
                  setError("");
                }}
                disabled={busy}
              >
                Rename
              </button>
            </div>
          )}
        </div>

        {flagged && <div className="text-sm text-secondary-light mt-2">{flaggedLine(review)}</div>}
        {flagged && canEdit && !renaming && (
          <div className="text-sm text-secondary-light mt-1" data-testid="name-review-teaches">
            {looksFineTeachesLine(user && user.fullName)}
          </div>
        )}

        {renaming && (
          <form onSubmit={rename} className="mt-12" noValidate>
            <ul className="text-sm text-secondary-light mb-12 ps-20" style={{ listStyle: "disc", lineHeight: 1.6 }}>
              <li>The name is saved with the same rules as sign-up: tidied, and capitals in normal case. A name the rules refuse is not saved.</li>
              <li>Other members see the first word, in chat too.</li>
              <li>{RENAME_TEACHES_LINE}</li>
              <li>Nothing is sent to {who}.</li>
            </ul>
            <div className="mb-12">
              <label className="form-label mb-4" htmlFor="name-review-new-name">
                New name
              </label>
              <input
                id="name-review-new-name"
                className="form-control"
                type="text"
                maxLength={100}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError("");
                }}
                disabled={busy}
              />
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setRenaming(false);
                  setName("");
                  setError("");
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
                {busy ? "Saving..." : "Save new name"}
              </button>
            </div>
          </form>
        )}

        {error && (
          <div className="text-danger text-sm mt-2" role="alert">
            {error}
          </div>
        )}

        {result && (
          <div className="alert alert-success mt-12 mb-0 py-12 px-16 text-sm" role="status">
            <div className="d-flex justify-content-between gap-2">
              <div className="fw-semibold">
                {result.kind === "renamed"
                  ? `Renamed to "${result.name}". Other members see "${firstNameOf(result.name)}".`
                  : result.data && result.data.already
                  ? "This name had already been looked at."
                  : `"${result.name}" is marked as looking fine.`}
              </div>
              {onDismissResult && (
                <button
                  type="button"
                  className="btn btn-sm p-0 border-0 bg-transparent text-success-600"
                  aria-label="Dismiss"
                  onClick={onDismissResult}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NameReviewPanel;
